import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { BehaviorSubject, EMPTY, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, mergeMap, takeUntil } from "rxjs/operators";
import { AddressDTO } from "src/app/data/schema/Common/AddressDTO";
import { ApiBillingCustomerService } from "src/app/data/service/billing/api-billing-customer.service";
import { ApiBillingZipCodesService } from "src/app/data/service/billing/api-billing-zip-codes.service";
import enUS from "./i18n/en-US.json";
import esES from "./i18n/es-ES.json";
import ptBR from "./i18n/pt-BR.json";

@Component({
    selector: "app-address-form",
    templateUrl: "./address-form.component.html",
    styleUrls: ["./address-form.component.scss"]
})
export class AddressFormComponent implements OnInit, OnDestroy {
    @Input("Address") Address: AddressDTO;
    @Input("IsRequired") isRequired: boolean;

    @ViewChild("inputNumber") inputNumber: ElementRef;

    public form: UntypedFormGroup;
    public zipCode$: BehaviorSubject<string>;

    private _unsubscribeAll = new Subject();

    constructor(
        private _apiBillingCustomerService: ApiBillingCustomerService,
        private _apiBillingZipCodesService: ApiBillingZipCodesService,
        private _spinner: NgxSpinnerService,
        private _translateService: TranslateService
    ) {
        this.zipCode$ = new BehaviorSubject<string>(null);

        this._translateService.setTranslation("pt-BR", ptBR, true);
        this._translateService.setTranslation("en-US", enUS, true);
        this._translateService.setTranslation("es-ES", esES, true);

        this.Address = this.Address ?? {
            city: "",
            complement: "",
            country: "",
            neighborhood: "",
            number: "",
            state: "",
            street: "",
            zipCode: ""
        };
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.complete();
    }

    ngOnInit(): void {
        this.form = new UntypedFormGroup({
            street: new UntypedFormControl(this.Address?.street ?? "", this.isRequired ? [Validators.required] : null),
            number: new UntypedFormControl(this.Address?.number ?? "", this.isRequired ? [Validators.required] : null),
            complement: new UntypedFormControl(this.Address?.complement ?? ""),
            neighborhood: new UntypedFormControl(
                this.Address?.neighborhood ?? "",
                this.isRequired ? [Validators.required] : null
            ),
            zipCode: new UntypedFormControl(this.Address?.zipCode ?? "", this.isRequired ? [Validators.required] : null),
            country: new UntypedFormControl(this.Address?.country ?? "", this.isRequired ? [Validators.required] : null),
            state: new UntypedFormControl(this.Address?.state ?? "", this.isRequired ? [Validators.required] : null),
            city: new UntypedFormControl(this.Address?.city ?? "", this.isRequired ? [Validators.required] : null)
        });

        this.zipCode$
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(500),
                distinctUntilChanged(),
                mergeMap((zipCode) => {
                    if ((zipCode || "").length == 8) {
                        this._spinner.show("loading-address");
                        return this._apiBillingZipCodesService.getAddressByZipCode(zipCode);
                    } else return EMPTY;
                })
            )
            .subscribe(
                (address: any) => {
                    this._spinner.hide("loading-address");
                    if (address) {
                        this.Address = {
                            city: address?.city || "",
                            complement: address?.additionalDetails || "",
                            country: address?.country || "",
                            number: address?.number || "",
                            neighborhood: address?.neighborhood || "",
                            state: address?.state || "",
                            zipCode: address?.zipcode || "",
                            street: address?.street || ""
                        };

                        this.inputNumber.nativeElement.focus();
                    }
                },
                () => {
                    this._spinner.hide("loading-address");
                }
            );
    }

    public exportValues(): AddressDTO {
        return this.Address;
    }
}
